import { type Entrance } from '@parkingbase/ecommerce-api-dev';

/** The address fields a garage and an entrance both carry, in the order they read. */
interface AddressLike {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/**
 * Builds a human-readable address from the device entrance's address record.
 *
 * ## Invariants:
 * - Every part is trimmed before the blank check. Why: the Back Office writes an untouched
 *   entrance as a fully-populated object of empty strings, so `filter(Boolean)` on the raw values
 *   still yields a string of commas — the entrance LOOKS configured and renders as punctuation.
 * - Lives here rather than in either caller. Why: the QR scan and the touchless ticket both show
 *   the entrance a parker is standing at, and the empty-string trap above is the kind of detail a
 *   second copy silently drops.
 */
export function buildEntranceAddress(entrance?: Entrance | null): string {
  return joinAddress(entrance?.address);
}

/**
 * Builds the garage's own street address — the fallback when the entrance has none.
 *
 * Takes a loose record because the two callers hold the garage as different shapes (a typed
 * `GarageModel` on the QR side, an untyped gate-arm payload on the touchless side).
 */
export function buildGarageAddress(garage?: Record<string, unknown> | null): string {
  if (!garage) return '';
  return joinAddress({
    address: garage.address as string | undefined,
    city: garage.city as string | undefined,
    state: garage.state as string | undefined,
    zip: garage.zip as string | undefined,
  });
}

function joinAddress(address?: AddressLike | null): string {
  if (!address) return '';
  return [address.address, address.address2, address.city, address.state, address.zip]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * The entrance's map coordinates, or undefined when it has none.
 *
 * ## Invariants:
 * - `0, 0` is treated as UNSET, not as a location. Why: an unconfigured entrance serialises its
 *   coordinates as `0.0` rather than omitting them, and Null Island is 1,000 miles off the coast
 *   of Africa — a directions link there is worse than no link, because the parker follows it.
 */
export function resolveEntranceCoordinates(
  entrance?: Entrance | null
): { latitude: number; longitude: number } | undefined {
  const latitude = entrance?.latitude;
  const longitude = entrance?.longitude;
  if (latitude === undefined || longitude === undefined) return undefined;
  if (latitude === 0 && longitude === 0) return undefined;
  return { latitude, longitude };
}

/**
 * The address to show for a scanned or ticketed location: the entrance the parker is standing at,
 * falling back to the garage.
 *
 * ## Invariants:
 * - The ENTRANCE wins whenever it has one. Why: a garage can expose several entrances on different
 *   streets, so its centroid address can send a parker to the wrong side of the block.
 * - The garage is a fallback, not a co-equal. Why: an operator who has configured the entrance has
 *   said where the parker is; one who has not still needs *an* address, and a roughly-right garage
 *   address beats a blank line. This replaces an earlier entrance-only rule on the QR side that
 *   showed nothing at all when the entrance was unconfigured (PB-4639).
 */
export function resolveLocationAddress(
  entrance?: Entrance | null,
  garage?: Record<string, unknown> | null
): string {
  return buildEntranceAddress(entrance) || buildGarageAddress(garage);
}

/**
 * The map/directions destination for a scanned or ticketed location.
 *
 * ## Invariants:
 * - Mirrors {@link resolveLocationAddress}'s precedence exactly. Why: the pin and the address are
 *   read together, and a pin at the garage centroid under an entrance address is the one
 *   combination that actively misleads — it looks confirmed by two sources.
 * - Both keys are always present, even when undefined. Why: callers spread this into result
 *   objects whose own types declare the keys as required-but-possibly-undefined, and optional keys
 *   do not satisfy those.
 * - Falls back only when the entrance pair is absent or Null Island. Why: a partly-configured
 *   entrance (one coordinate set) is a configuration error, not a location, so it must not be
 *   mixed with the garage's other coordinate into a point that exists nowhere.
 */
export function resolveLocationCoordinates(
  entrance?: Entrance | null,
  garage?: Record<string, unknown> | null
): { latitude: number | undefined; longitude: number | undefined } {
  const fromEntrance = resolveEntranceCoordinates(entrance);
  if (fromEntrance) return fromEntrance;
  return {
    latitude: garage?.latitude as number | undefined,
    longitude: garage?.longitude as number | undefined,
  };
}
